import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Compass, Pencil } from "lucide-react";
import { ExperimentCard } from "@/v2/Growth";
import { GoalPulse } from "@/v2/Pull";
import { WinButton } from "@/v2/today3/WinCard";

const EXPAND = { initial: { height: 0, opacity: 0 }, animate: { height: "auto", opacity: 1 }, exit: { height: 0, opacity: 0 } };

const TODAY_STATE = { done: ["Moved forward today", "text-[#4F6B55]"], planned: ["In today's must-dos", "text-[#2D2926]"], none: ["Not in today's plan", "text-[#8C857B]"] };

function GoalLine({ g, i, month, musts }) {
  const m = musts.find((x) => x.goal_id === g.id || x.link?.goal_id === g.id);
  const [label, cls] = TODAY_STATE[m ? (m.status === "done" ? "done" : "planned") : "none"];
  return (
    <li data-testid={`ladder-goal-${i}`}>
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="font-semibold text-[#2D2926] truncate">{g.title}</span>
        <span className="font-mono text-xs text-[#635E59] shrink-0" data-testid={`ladder-goal-progress-${i}`}>{g.progress}/{g.target}</span>
      </div>
      {g.action && <p className="text-xs text-[#635E59] mt-1" data-testid={`ladder-goal-action-${i}`}>Daily action: {g.action}{g.minutes ? ` · ${g.minutes} min` : ""} · <span className={`font-semibold ${cls}`} data-testid={`ladder-goal-today-${i}`}>{label}</span></p>}
      <div className="h-1.5 mt-1.5 rounded-full bg-[#EDE7DD] overflow-hidden"><motion.div className="h-full bg-[#C86D51]" initial={{ width: 0 }} animate={{ width: `${g.pct}%` }} /></div>
      {g.progress >= g.target && <div className="mt-2"><WinButton goal={g} month={month} compact testid={`ladder-goal-share-${i}`} /></div>}
    </li>
  );
}

function summary(board) {
  const n = board.goals.length;
  if (!n) return `No ${board.month_label} goals yet`;
  const pct = Math.round(board.goals.reduce((a, g) => a + (g.pct || 0), 0) / n);
  return `${n} ${board.month_label} goal${n === 1 ? "" : "s"} · ${pct}% of the way`;
}

export function BiggerPicture({ board, t, s, onEdit, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="card" data-testid="goal-ladder-widget">
      <button onClick={() => setOpen(!open)} aria-expanded={open} data-testid="goal-ladder-toggle" className="w-full min-h-[64px] p-4 sm:p-5 flex items-center justify-between gap-3 text-left">
        <span className="min-w-0">
          <span className="eyebrow block">Your bigger picture</span>
          <span className="block text-sm font-semibold text-[#2D2926] mt-1 truncate" data-testid="bigger-picture-summary">{summary(board)}</span>
        </span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-[#635E59] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>{open && (
        <motion.div {...EXPAND} className="overflow-hidden" data-testid="goal-ladder-more">
          <div className="px-4 pb-4 sm:px-5 sm:pb-5 space-y-5 border-t border-[#EFECE6] pt-4">
            <div data-testid="ladder-vision">
              <span className="eyebrow block">Long-term vision</span>
              <p className="text-sm text-[#2D2926] leading-relaxed mt-1.5" data-testid="ladder-vision-text">{board.vision_line || "Write your vision on Journey to see it here."}</p>
            </div>
            <div data-testid="ladder-month">
              <div className="flex items-center justify-between gap-3">
                <span className="eyebrow">{board.month_label} goals · days done / target</span>
                {board.goals.length > 0 && <button onClick={onEdit} data-testid="goal-ladder-edit" className="h-11 -my-2 px-2 text-xs font-semibold text-[#635E59] inline-flex items-center gap-1"><Pencil className="h-3.5 w-3.5" />Edit</button>}
              </div>
              {board.goals.length
                ? <ul className="space-y-3 mt-2">{board.goals.map((g, i) => <GoalLine key={g.id} g={g} i={i + 1} month={board.month_label} musts={board.must_dos} />)}</ul>
                : <button onClick={onEdit} data-testid="ladder-set-goals" className="mt-2 h-11 px-5 rounded-full border border-stone-300 text-sm font-semibold">Set this month's goals</button>}
            </div>
            <GoalPulse pulse={t.pulse} onChange={onChange} />
            {t.day?.shape !== "experiment" && <ExperimentCard goal={t.pulse?.goal} featured={false} />}
            <Link to="/journey" data-testid="ladder-phase-link" className="min-h-11 flex items-center gap-2 text-xs text-[#635E59]"><Compass className="h-4 w-4" />{s.phase.name} · week {s.week} of your year</Link>
          </div>
        </motion.div>
      )}</AnimatePresence>
    </section>
  );
}
